import { Box } from '@factorialco/deprecated-design-system'
import type moment from 'moment'

import { observer } from 'mobx-react'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import ShiftCellActions from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CellActions/ShiftCellActions'
import ShiftDetails from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/ShiftCell/BaseShift/ShiftLayout/ShiftDetails'
import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { useDragShift } from 'modules/shift_management/hooks/useDragAndDrop'
import { usePreview } from 'modules/shift_management/hooks/usePreview'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type { EmployeeWithShifts, Shift, ShiftsRow } from 'modules/shift_management/lib/types'
import { usePlannedBreaks } from 'modules/time_planning/store/internal/collections/plannedBreaks'
import type PlannedBreak from 'modules/time_planning/store/internal/models/PlannedBreak'

import CompactView from './CompactView'

export interface PropsLayout {
  shiftsRow: ShiftsRow
  shift: Shift
  date: moment.Moment
  employee: EmployeeWithShifts
  isSelectionEnabled: boolean
  isSelected: boolean
  previewPlannedBreaks?: PlannedBreak[] | null
  preview: boolean
  contractVersions: ContractVersions
}

const ShiftLayout = ({
  shiftsRow,
  shift,
  date,
  employee,
  isSelectionEnabled,
  isSelected,
  previewPlannedBreaks = null,
  preview,
  contractVersions,
}: PropsLayout) => {
  const { viewOptions } = useCurrentViewOptions()
  const hideBackupShifts = !viewOptions.other.showBackupShifts
  const isBackup = shift.isBackup
  const canDragAndDrop = !isBackup
  const [{ view }] = useSchedule()
  const [{ shifts: previewShifts, shiftIdsToHide }] = usePreview()
  const {
    schedule: { showCompactView },
  } = viewOptions
  // TODO: Remove this once 'dev_shift_management_graphql_migration' is done to use shift.plannedBreaks
  const plannedBreaksCollection = usePlannedBreaks()
  const plannedBreaks =
    previewPlannedBreaks ||
    shift.plannedBreaks?.plannedBreaks ||
    plannedBreaksCollection.filterByShift(shift.id)

  const isHidden = useUnsafeMemo(
    () => previewShifts.length && shiftIdsToHide.includes(shift.id),
    [previewShifts, shiftIdsToHide, shift.id]
  )

  const { isDragging, drag } = useDragShift({
    shiftsRow: shiftsRow,
    shift,
    options: { dropEffect: 'move' },
  })

  if (!preview && isBackup && hideBackupShifts) return null

  return (
    <Box
      // Can't use maxWidth prop directly because gamma does not have s170 as a dimension size
      // which is necessary to accommodate the new cell design
      style={{
        opacity: canDragAndDrop && isDragging ? '0.5' : '1.0',
        transform: canDragAndDrop ? 'translate(0, 0)' : undefined,
        // When isHidden on edition, we should not display the shift but keep the component
        // because the sidepanel is rendered inside the shift, otherwise, the sidepanel would be hidden.
        display: isHidden ? 'none' : 'flex',
      }}
      flexDirection="row"
      overflowX="hidden"
      minHeight={view.cellMinHeight}
      ref={canDragAndDrop ? drag : undefined}
    >
      <ShiftCellActions
        shift={shift}
        selectedDay={date}
        employee={employee}
        isSelectionEnabled={isSelectionEnabled}
        isSelected={isSelected}
        preview={preview}
        contractVersions={contractVersions}
      >
        {showCompactView && <CompactView shift={shift} date={date} plannedBreaks={plannedBreaks} />}
        {!showCompactView && (
          <ShiftDetails shift={shift} date={date} plannedBreaks={plannedBreaks} preview={preview} />
        )}
      </ShiftCellActions>
    </Box>
  )
}

export default observer(ShiftLayout)
